import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import { ArrowLeft } from '../../components/icons/arrow-left';

const ARRANGEMENTS_MARKDOWN = `
**Расстановки** — это метод системной психотерапии, позволяющий увидеть и скорректировать скрытую динамику, которая влияет на жизнь. На платформе ZiNGiZi этот метод реализован через **визуальное пространство поля**, где фигурки становятся заместителями членов системы.

*Вам не нужна группа. Не нужны живые заместители. Только вы, цифровое пространство поля и фигурки — и возможность увидеть правду о своей семье, отношениях или бизнесе.*

## Как проходят расстановки на ZiNGiZi

Вы заходите в инструмент, выбираете цвет поля (фона) и получаете стартовый набор из 20 фигур: люди, дома, деревья, животные. Затем вы:

1. **Назначаете** каждой фигурке роль — «мама», «папа», «я», «партнёр», «деньги», «болезнь» и т.д.
2. **Расставляете** их на поле так, как чувствуете — кто где стоит, куда смотрит, на каком расстоянии.
3. **Наблюдаете** — где напряжение? Кто стоит в пустоте? Кто лишний?
4. **Перемещаете** фигурки, добавляете новые, убираете лишние — до появления внутреннего чувства «правильно».
5. **Проговариваете** за фигурки ключевые фразы: «Ты имеешь место», «Я большой, а ты маленький», «Я отпускаю тебя».

Это и есть расстановка в онлайн-формате. Глубокая, честная и безопасная.

## Уникальные преимущества расстановок на ZiNGiZi

### 1. Клиент и фигурки

Классические расстановки часто требуют присутствия заместителей (других людей). Это дорого, сложно логистически и психологически страшно. На ZiNGiZi клиент расставляет **символы самостоятельно**. Вся система — перед клиентом, и он полностью контролирует процесс.

### 2. Полная конфиденциальность

Семейные тайны — исключённые дети, аборты, измены, преступления, суициды — остаются только между клиентом, психологом и полем. Никто из посторонних не увидит расстановку. Клиент предъявляет ровно столько, сколько готов.

### 3. Фигурка не спорит и не добавляет своего

Живой заместитель может привнести свои чувства, интерпретации или усталость. Фигурка на ZiNGiZi — **идеальный носитель роли**. Она делает ровно то, что клиент ей назначил, и не искажает системную информацию.

### 4. Мгновенная проверка гипотез

Перемещение фигурки отца ближе — стало легче? Отодвинули «работу» — появилась тревога? Клиент может **десятки раз перестраивать систему за одну сессию**, мгновенно проверяя, что приносит облегчение, а что усиливает напряжение.

### 5. Выбор цвета фона — диагностический ключ

В расстановках цвет поля задает клиент, и он (фон) несёт смысл:

- **Синий** (вода, небо) — работа с глубиной, бессознательным, страхами, темой утопленников, выкидышей.
- **Чёрный** — утрата, смерть, траур, исключённые тени, тяжёлые родовые тайны.
- **Зелёный** — жизнь, рост, исцеление рода, восстановление связей.
- **Бежевый** — нейтральная основа для структурных и бизнес-расстановок.

Выбор цвета — это **дополнительный инструмент**, помогающий точнее настроиться на запрос.

### 6. Расстановка во времени (эффект дневника)

Композиция сохраняется, и клиент может вернуться к ней **через неделю, месяц или год**. Изменилось ли что-то в жизни? Попробовать подвигать фигурки снова и сравнить два результата. Это позволяет отслеживать реальные системные изменения — то, что невозможно в офлайн-формате, где поле после сессии разрушают.

### 7. Безопасная работа со сложными темами

Темы суицида, убийства, инцеста, тяжёлых болезней, насилия — в групповой расстановке они могут стать травматичными из-за присутствия других людей. В песочнице ZiNGiZi клиент даёт **ровно столько информации, сколько готов предъявить**. Фигурки не задают лишних вопросов и не осуждают.

### 8. Организационные расстановки без привлечения сотрудников

Для бизнес-расстановок не нужно собирать отдел или приглашать коллег. Клиент расставляет **должности, проекты, отделы, партнёров** с помощью фигурок. Это честно, конфиденциально и позволяет увидеть скрытые конфликты в иерархии.

## С какими запросами можно работать

Расстановки на ZiNGiZi эффективны для всех классических системных запросов.

### Семья и родовые сценарии

| Запрос | Как это работает в расстановке |
| --- | --- |
| **Повторяющиеся судьбы** (все разведены, мужчины умирают рано, женщины болеют одним и тем же) | Клиент выставляет фигурки поколений. Видит, кто исключён из системы. Добавляет исключённого — симптом часто уходит. |
| **Холодность или конфликт с матерью/отцом** | Ставит фигурку родителя и себя. Смотрит на дистанцию. Меняет расположение, пока не появится чувство «правильно». |
| **Трудности с сепарацией (не могу отделиться от родителей)** | Фигурки «я» и «мама» стоят слишком близко. Добавляете фигурку «партнёр» или «работа» как мостик во взрослую жизнь. |
| **Исключённые дети (аборты, выкидыши, умершие в раннем возрасте)**| Кладёт фигурку (камень, дом, дерево) на свободное место. Произносит: «Ты имеешь место в нашей системе». Часто это снимает бесплодие, чувство вины или хронические болезни. |
| **Тяжёлая судьба предков (репрессии, эмиграция, голод, потеря всего)** | Ставит фигурку предка отдельно. Смотрит на неё с уважением. Произносит: «Через тебя я есть. Я помню. Я живу свою жизнь». |

### Партнёрские отношения

- **Выбор абьюзивных или эмоционально холодных партнёров** — в расстановке видно, что клиент «подселяете» в партнёра фигурку отца или матери, пытаясь «исправить» детскую травму.
- **Не могу создать пару (одиночество)** — часто на месте партнёра стоит пустота или фигурка «карьера», «мама», «мой бывший, которого не отпустил».
- **Измены, эмоциональная зависимость, созависимость** — ищет, кто из предыдущих партнёров не «отпущен». Добавляет фигурку бывшего с фразой: «Я благодарю тебя за наше время, но теперь я иду дальше».
- **Развод - не могу отпустить, зациклен(а) на бывшем** — расставляет систему «я, бывший, наша история», добавляет фигурку «моя новая жизнь» на горизонте.

### Психосоматика и здоровье

- **Бесплодие, выкидыши, неудачные ЭКО** — почти всегда в системе есть исключённый ребёнок (аборт у вас или у вашей матери, ранняя смерть сиблинга). Расстановка даёт ему место - и тело часто «разрешает» беременность.
- **Аллергии, астма, кожные заболевания** — часто фигурка «тот, на кого я не хочу смотреть» стоит прямо напротив.
- **Аутоиммунные заболевания** — агрессия, направленная на себя. В расстановке ищет агрессора в роду и возвращает ему его чувства через фигурку и фразу: «Это твоя злость, не моя. Я возвращаю её тебе»..
- **Мигрени, хроническая усталость, депрессия** — часто взгляд фигурки клиента направлен на умершего предка («я уйду за тобой»). Разворачивает взгляд на живых, произносите: «Я остаюсь здесь, среди живых».
.

### Деньги, работа, самореализация

- **Хроническая нехватка денег, долги, блок на доход** — расстановка позволяет разделить чужие и свои судьбы.
- **Синдром самозванца, страх успеха** —  ставите фигурку «мой успех» и смотрите, кто из семьи на неё косо смотрит или отворачивается. Произносит: «Мой успех не предательство по отношению к вам».
- **Конфликт с начальником, подчинёнными, бизнес-партнёрами** — организационная расстановка ролей и иерархии.
- **Не могу закончить проект, прокрастинация** — добавляет фигурку «незавершённое дело предка». Завершает его символически фразой: «Я сделаю то, что не смог(ла) сделать ты. А потом проживу свою жизнь».

### Потери и горевание

- **Затяжное горе (более 1–2 лет) после смерти близкого** — расставляет живых и умершего, произносит: «Я остаюсь, а ты уходишь. Я буду чтить тебя в своём сердце, но жить буду среди живых».
- **Потеря ребёнка, выкидыш, мертворождение** — кладёт фигурку в угол поля, рядом ставите цветок или дерево (символ памяти). Произносит: «Ты имеешь место в моей душе. Я никогда тебя не забуду. И я живу дальше».

### Экзистенциальные и внутренние запросы

- **Кризис идентичности («кто я без ролей?»)** — расставляет все свои роли (мать, отец, сотрудник, сын/дочь, партнёр) вокруг фигурки «Я». Убирает лишнее, смотрите, что остаётся.
- **Страх смерти, навязчивые мысли о смерти** — ставит фигурку «смерть» в угол и смотрит, кто из предков тянется к ней. Произносит: «Я остаюсь здесь. Моё место — среди живых».
- **Чувство вины без видимых причин** — ищет исключённого члена системы, чью судьбу вы бессознательно повторяет. Даёт ему место — и чувство вины часто уходит.

---

## Как психологу провести расстановку на ZiNGiZi

Это готовая пошаговая схема для одной сессии.

| Шаг | Действие |
| --- | --- |
| 1 | Вместе с клиентом выберите цвет песочницы под запрос (синий — глубина/страх, чёрный — траур/утрата, зелёный — рост/исцеление, бежевый — нейтральный). |
| 2 | Попросите клиента назначить фигурки: «Кто в этой расстановке будет мамой? Кто папой? Кем будешь ты?». |
| 3 | Клиент расставляет фигурки в песочнице так, «как есть сейчас» (без анализа, просто чувствуя). |
| 4 | Задайте вопросы для диагностики: «Кто чувствует себя лишним? Кто отвернут? Кто смотрит в пустоту? Кто стоит слишком близко/далеко?». |
| 5 | Предложите добавить исключённых (абортированные дети, умершие рано, «позорные» родственники) — просто поставьте фигурку на свободное место. |
| 6 | Попросите клиента перемещать фигурки до появления внутреннего чувства «правильно» / «спокойно» / «легче». |
| 7 | Предложите ключевые фразы-разрешения (проговаривая от себя или от лица фигурки):<ul><li>«Ты имеешь место в нашей системе».</li><li>«Я большой, а ты маленький» (для сепарации).</li><li>«Я возвращаю тебе твою судьбу».</li><li>«Я остаюсь среди живых».</li></ul> |
| 8 | Завершите расстановку. Спросите: «Что изменилось в теле / в чувствах?». Часто клиент физически ощущает облегчение.|

## Когда расстановки не используются (противопоказания)

Расстановки на ZiNGiZi (как и любые системные расстановки) **не рекомендуются** в следующих случаях:

- Острый психоз, галлюцинации, маниакальное состояние.
- Тяжёлая депрессия с активными суицидальными мыслями (требуется стабилизация психиатром).
- Острый период горя (первые 3–6 месяцев после тяжёлой утраты).
- Клиент находится в состоянии выраженного алкогольного или наркотического опьянения.
- Клиент категорически не готов увидеть системную правду (сильное отрицание, психотическая защита).

Во всех остальных случаях метод безопасен и эффективен — особенно в онлайн-формате через песочницу ZiNGiZi.

## Кому подходят расстановки на ZiNGiZi

| Психологам | Клиентам |
| --- | --- |
| Тем, кто хочет добавить мощный системный инструмент без необходимости организовывать групповые сессии. | Тем, кто боится групповых расстановок или не имеет возможности посещать их очно. |
| Тем, кто работает онлайн и ищет эффективные дистанционные методы для работы с родом, деньгами, психосоматикой. | Тем, кто устал от «разговоров» и хочет быстрого, глубинного результата. |
| Бизнес-консультантам для организационных расстановок без привлечения реальных сотрудников.| Тем, кто чувствует, что «что-то повторяется из поколения в поколение». |

---

«Расстановки — это способ увидеть невидимые связи в семье или жизни. Мы не будем приглашать посторонних людей. У нас есть цифровое поле и фигурки. Клиент назначает: вот это мама, это папа, это я. Расставляет их так, как чувствует. И вдруг становится видно — кто стоит спиной, кто смотрит в пустоту, кого не хватает. Мы добавляем забытых. Произносим важные фразы. И система приходит в порядок. Часто это занимает одну сессию. И это работает даже онлайн».
`;

const Arrangements = () => {
  return (
    <div className="pb-[80px]">
      <h2 className="flex items-center gap-[14px] font-comfortaa font-semibold text-xl text-blackBase mb-5">
        <Link to={'/tools'} className="group">
          <ArrowLeft className="fill-blackBase group-hover:fill-redBase" width={21} height={16} />
        </Link>
        <span>Расстановки</span>
      </h2>

      <div className="w-full mb-10">
        <div className="text-blackBase font-manrope text-sm leading-relaxed">
          <h2 className="text-xl font-semibold text-blackBase mb-4">Расстановки на ZiNGiZi — системная терапия</h2>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h2: (props) => <h2 className="text-xl font-semibold text-blackBase mt-8 mb-4 first:mt-0" {...props} />,
              h3: (props) => <h3 className="text-lg font-semibold text-blackBase mt-6 mb-3" {...props} />,
              p: (props) => <p className="mb-4 last:mb-0 text-greyBase" {...props} />,
              ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
              ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
              li: (props) => <li className="text-greyBase mt-2" {...props} />,
              hr: () => <hr className="my-8 border-t border-greyBase" />,
              strong: (props) => <strong className="font-semibold text-blackBase" {...props} />,
              table: (props) => (
                <div className="my-6 w-full">
                  <table className="w-full max-w-4xl border border-greyLight text-sm border-collapse" {...props} />
                </div>
              ),
              thead: (props) => <thead className="bg-greyLight" {...props} />,
              th: ({ children, ...props }) => (
                <th
                  className={`border border-greyLight px-4 py-3 text-left align-top font-semibold whitespace-nowrap text-blackBase${String(children).trim() === 'Шаг' ? ' w-10' : ''}`}
                  {...props}
                >
                  {children}
                </th>
              ),
              td: (props) => (
                <td className="border border-greyLight px-4 py-3 align-top leading-relaxed text-greyBase" {...props} />
              ),
            }}
          >
            {ARRANGEMENTS_MARKDOWN}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default Arrangements;
